import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Video, LogOut, Search, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Button } from '@/components/ui/button';

const Navbar: React.FC = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories
  });

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleAuthRequired = (e: React.MouseEvent) => {
    if (!user) {
      e.preventDefault();
      toast({
        title: "Sign in required",
        description: "Please sign in to access this feature"
      });
      navigate('/auth');
    }
  };

  return (
    <nav className="border-b border-border py-4">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-accent rounded-md p-1">
              <Video size={24} className="text-background" />
            </div>
            <h1 className="text-xl font-bold">Video Curator</h1>
          </Link>

          <div className="flex items-center gap-4 ml-8">
            <Button
              variant={isActive('/') ? "default" : "ghost"}
              asChild
            >
              <Link to="/">Home</Link>
            </Button>

            <Button
              variant={isActive('/search') ? "default" : "ghost"}
              asChild
            >
              <Link to="/search">
                <Search size={16} className="mr-2" />
                Search
              </Link>
            </Button>

            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={isActive('/categories') ? "bg-accent text-accent-foreground" : ""}
                  >
                    Categories
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid grid-cols-4 gap-3 p-4 w-[600px]">
                      {categories.map((category) => (
                        <li key={category.id}>
                          <Link
                            to={`/category/${category.slug}`}
                            className="block select-none rounded-md p-2 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none mb-1">{category.title}</div>
                            {category.description && (
                              <p className="line-clamp-1 text-xs leading-snug text-muted-foreground">
                                {category.description}
                              </p>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>
          
          <div className="ml-auto">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback>
                        <User className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/manage">My Collections</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/paths">Learning Paths</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => signOut()} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" asChild>
                <Link to="/auth">Sign In</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;